import { useTranslation } from "@/hooks";
import { useBookmarksContext } from "@/stores/bookmark";
import { useActiveDataflow, useActiveDataflowId } from "@/stores/dataflow";
import { useGroupState, useMapStore } from "@/stores/map";
import { useUIStore } from "@/stores/ui";
import { useUserFiles } from "@/stores/user-file";
import { TREE_NODE_IDS } from "@/utils/constants/map";
import { getInputDataNameFromFlow } from "@/utils/data/getInputDataName";
import {
  Box,
  Flex,
  Tree,
  useTree,
  type RenderTreeNodePayload,
} from "@mantine/core";
import { useCallback, useMemo } from "react";
import { LayerTreeNode } from "./components/LayerTreeNode";
import { useColumnOptions, useProcessedGroups, useUniqueValues } from "./hooks";
import type { LayerTreeNodeData } from "./types";
import { buildMyDataNode, buildStaticLayersNode } from "./utils/treeData";
import "./WorkspaceSidebarLayers.css";

export function WorkspaceSidebarLayers() {
  const { t, currentLanguage } = useTranslation();
  const activeDataflow = useActiveDataflow();
  const activeDataflowId = useActiveDataflowId();
  const userFiles = useUserFiles();
  const { getBookmarkFromTableId } = useBookmarksContext();

  const {
    staticLayers,
    geoJSONLayers,
    toggleStaticLayer,
    toggleDataLayer,
    updateLayerVisibility,
    setGroupColumnName,
    setVisibleGroups,
    getGroupConfig,
  } = useMapStore((state) => state);
  const openModal = useUIStore((state) => state.openModal);
  const groupState = useGroupState(activeDataflowId);
  const groupColumnName = groupState?.groupColumnName ?? null;
  const visibleGroups = groupState?.visibleGroups ?? [];
  const visualizationMode = groupState?.visualizationMode;
  const visualizationLayerType = groupState?.visualizationLayerType;
  const columnOptions = useColumnOptions(activeDataflowId);
  const uniqueValues = useUniqueValues(activeDataflowId, groupColumnName);
  const processedGroups = useProcessedGroups(
    groupColumnName,
    uniqueValues,
    groupState
  );

  const inputDataName = useMemo(() => {
    return (
      getInputDataNameFromFlow(
        activeDataflow?.flow_definition_json,
        userFiles,
        getBookmarkFromTableId,
        currentLanguage
      ) ?? null
    );
  }, [
    activeDataflow?.flow_definition_json,
    userFiles,
    getBookmarkFromTableId,
    currentLanguage,
  ]);

  // Build tree data
  const treeData: LayerTreeNodeData[] = useMemo(() => {
    const items: LayerTreeNodeData[] = [
      buildStaticLayersNode(t, toggleStaticLayer),
    ];

    if (inputDataName) {
      items.push(
        buildMyDataNode(
          inputDataName,
          groupColumnName,
          processedGroups,
          toggleDataLayer,
          updateLayerVisibility,
          activeDataflowId,
          visualizationMode,
          visualizationLayerType
        )
      );
    }

    return items;
  }, [
    t,
    inputDataName,
    groupColumnName,
    processedGroups,
    toggleStaticLayer,
    toggleDataLayer,
    updateLayerVisibility,
    activeDataflowId,
  ]);

  // Simplified tree initialization - use direct store subscriptions
  // Tree state is managed by Mantine's useTree hook, which handles its own state
  // We just need to provide initial data and let the tree manage its own state
  const tree = useTree({
    initialCheckedState: [],
    initialExpandedState: {
      // Auto-expand folders if they have visible children
      [TREE_NODE_IDS.STATIC_LAYERS]: Object.values(staticLayers).some((v) => v),
      [TREE_NODE_IDS.MY_DATA]: !!inputDataName,
      // Always expand the data layer node and column selector node when there's input data
      [TREE_NODE_IDS.MY_DATA_INPUT]: !!inputDataName,
      [TREE_NODE_IDS.MY_DATA_INPUT_GROUP]: !!inputDataName,
    },
  });

  // Memoize render node callback
  const renderNode = useCallback(
    (payload: RenderTreeNodePayload) => {
      return (
        <LayerTreeNode
          payload={payload}
          staticLayers={staticLayers}
          geoJSONLayers={geoJSONLayers}
          visibleGroups={visibleGroups}
          processedGroups={processedGroups}
          groupColumnName={groupColumnName}
          columnOptions={columnOptions}
          inputDataName={inputDataName}
          activeDataflowId={activeDataflowId}
          setGroupColumnName={setGroupColumnName}
          setVisibleGroups={setVisibleGroups}
          updateLayerVisibility={updateLayerVisibility}
          getGroupConfig={getGroupConfig}
          openModal={openModal}
        />
      );
    },
    [
      staticLayers,
      geoJSONLayers,
      visibleGroups,
      processedGroups,
      groupColumnName,
      columnOptions,
      inputDataName,
      activeDataflowId,
      setGroupColumnName,
      setVisibleGroups,
      updateLayerVisibility,
      getGroupConfig,
      openModal,
    ]
  );

  return (
    <Flex direction="column" flex={1} className="tree-container" h="100%">
      <Box flex={1} w="100%" miw={0} style={{ overflow: "scroll" }} p="md">
        <Tree
          data={treeData}
          tree={tree}
          levelOffset={12}
          renderNode={renderNode}
        />
      </Box>
    </Flex>
  );
}
